import { useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import DataTable from '../../components/DataTable';
import RequestsSummary from '../../components/RequestsSummary';
import PaymentDetail from '../../components/PaymentDetail';
import DealDetail from './DealDetail';
import {
  usePeriod, useGo, useBlock, BlockState, PeriodBar, money, fmtDate, formatHours,
} from './ceoShared';
import {
  AlertBlock, PnlBlock, BalancesCard, DebtsCard, ProjectsColumn, SalesColumn, OperationsBlock,
} from './CeoDashboard';

const REQUEST_STATUS = { new: 'Новая', pending: 'На подписи', paid: 'Оплачена', rejected: 'Отклонена' };
const READINESS = { in_progress: 'В работе', completed: 'Завершена', on_hold: 'Приостановлена', cancelled: 'Отменена' };
const DIRECTION = { income: 'Приход', expense: 'Расход' };

const num = (k) => (r) => Number(r[k]) || 0;
const moneyCol = (key, label, extra = {}) => ({
  key, label, render: r => money(r[key]), sortValue: num(key), filterable: false,
  summable: true, footerFormat: v => money(v), ...extra,
});
const dateCol = (key, label) => ({ key, label, render: r => fmtDate(r[key]), sortValue: r => r[key] || '', filterValue: r => fmtDate(r[key]) });

// ─── Наборы столбцов по типу строк ──────────────────────────────────────────────────────────
const REQUEST_COLUMNS = [
  { key: 'id', label: '#', sortValue: num('id'), filterable: false },
  dateCol('created_at', 'Создана'),
  { key: 'creator_name', label: 'Создатель' },
  { key: 'project', label: 'Проект' },
  { key: 'direction', label: 'Направление', render: r => r.direction || '—', filterValue: r => r.direction || '—' },
  { key: 'article', label: 'Статья', render: r => r.article || '—', filterValue: r => r.article || '—' },
  { key: 'recipient', label: 'Получатель', render: r => r.recipient || '—', filterValue: r => r.recipient || '—' },
  moneyCol('amount', 'Сумма'),
  { key: 'source', label: 'Источник' },
  { key: 'status', label: 'Статус', render: r => <span className={`badge badge-${r.status}`}>{REQUEST_STATUS[r.status]}</span>, filterValue: r => REQUEST_STATUS[r.status] },
  dateCol('date_requested', 'Оплатить до'),
  { key: 'days_overdue', label: 'Просрочка', sortValue: num('days_overdue'), filterable: false,
    render: r => (r.days_overdue > 0 ? <b style={{ color: 'var(--danger)' }}>{r.days_overdue} дн</b> : '—') },
];

const PROJECT_COLUMNS = [
  { key: 'name', label: 'Проект' },
  { key: 'manager_name', label: 'Менеджер', render: r => r.manager_name || '—', filterValue: r => r.manager_name || '—' },
  { key: 'customer_name', label: 'Заказчик', render: r => r.customer_name || '—', filterValue: r => r.customer_name || '—' },
  { key: 'readiness_status', label: 'Статус', render: r => READINESS[r.readiness_status], filterValue: r => READINESS[r.readiness_status] },
  moneyCol('budget', 'Бюджет'),
  moneyCol('received', 'Получено'),
  moneyCol('debt', 'Долг заказчика'),
  moneyCol('expenses', 'Расходы'),
  { key: 'marginPercent', label: 'Маржа', filterable: false, sortValue: r => r.marginPercent ?? -Infinity,
    render: r => (r.marginPercent == null ? '—' : <b style={r.marginPercent < 50 ? { color: 'var(--danger)' } : undefined}>{r.marginPercent.toFixed(0)}%</b>) },
  dateCol('completed_at', 'Завершён'),
];

const ENTRY_COLUMNS = [
  dateCol('entry_date', 'Дата'),
  { key: 'direction', label: 'Тип', render: r => DIRECTION[r.direction], filterValue: r => DIRECTION[r.direction] },
  { key: 'direction_name', label: 'Направление', render: r => r.direction_name || '—', filterValue: r => r.direction_name || '—' },
  { key: 'article_name', label: 'Статья', render: r => r.article_name || '—', filterValue: r => r.article_name || '—' },
  { key: 'project_name', label: 'Проект', render: r => r.project_name || '—', filterValue: r => r.project_name || '—' },
  { key: 'contractor_name', label: 'Контрагент', render: r => r.contractor_name || '—', filterValue: r => r.contractor_name || '—' },
  moneyCol('amount', 'Сумма'),
  { key: 'bank_account_name', label: 'Счёт', render: r => (r.bank_account_name || r.account_label || '—').replace(/^Точка\s*—\s*/, ''), filterValue: r => (r.bank_account_name || r.account_label || '—').replace(/^Точка\s*—\s*/, '') },
  { key: 'note', label: 'Назначение', filterable: false, render: r => r.note || '—' },
];

const ARTICLE_COLUMNS = [
  { key: 'direction_name', label: 'Направление', render: r => r.direction_name || '—', filterValue: r => r.direction_name || '—' },
  { key: 'article_name', label: 'Статья' },
  moneyCol('income', 'Доход'),
  moneyCol('expense', 'Расход'),
  { key: 'result', label: 'Итог', filterable: false, sortValue: r => Number(r.income) - Number(r.expense), summable: true,
    footerFormat: v => money(v), render: r => money(Number(r.income) - Number(r.expense)) },
];

const BANK_COLUMNS = (navigate) => [
  dateCol('date', 'Дата'),
  { key: 'direction', label: 'Тип', render: r => DIRECTION[r.direction], filterValue: r => DIRECTION[r.direction] },
  moneyCol('amount', 'Сумма'),
  { key: 'contractor_name', label: 'Контрагент', render: r => r.contractor_name || '—', filterValue: r => r.contractor_name || '—' },
  { key: 'purpose', label: 'Назначение', filterable: false, render: r => r.purpose || '—' },
  { key: 'bank_account_name', label: 'Счёт', render: r => (r.bank_account_name || '—').replace(/^Точка\s*—\s*/, ''), filterValue: r => (r.bank_account_name || '—').replace(/^Точка\s*—\s*/, '') },
  { key: 'reason', label: 'Где', filterValue: r => r.reason },
  { key: 'action', label: '', filterable: false, sortable: false,
    render: r => (
      <button className="btn-primary btn-sm" onClick={e => { e.stopPropagation(); navigate(String(r.id).startsWith('bank-') ? '/finance/categorization' : '/finance/cashflow'); }}>
        Разметить
      </button>
    ) },
];

const DEAL_COLUMNS = [
  dateCol('createdDate', 'Создана'),
  { key: 'name', label: 'Сделка' },
  moneyCol('price', 'Сумма'),
  { key: 'pipeline', label: 'Воронка' },
  { key: 'stage', label: 'Стадия' },
  { key: 'responsible', label: 'Ответственный' },
  { key: 'hours_to_first_call', label: 'До первого звонка', filterable: false, sortValue: r => r.hours_to_first_call ?? -1, render: r => formatHours(r.hours_to_first_call) },
  { key: 'hours_waiting', label: 'Висит', filterable: false, sortValue: num('hours_waiting'),
    render: r => (r.is_open ? <span style={r.hours_waiting > 48 && !r.firstCallAt ? { color: 'var(--danger)' } : undefined}>{formatHours(r.hours_waiting)}</span> : '—') },
  { key: 'url', label: '', filterable: false, sortable: false,
    render: r => (r.url ? <a href={r.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>в AmoCRM ↗</a> : null) },
];

const DESIGNER_COLUMNS = [
  { key: 'project_name', label: 'Проект' },
  { key: 'designer_name', label: 'Дизайнер' },
  { key: 'sketch_type', label: 'Тип эскиза', render: r => r.sketch_type || '—', filterValue: r => r.sketch_type || '—' },
  moneyCol('total_amount', 'Сумма'),
  dateCol('created_at', 'Назначено'),
  dateCol('completed_at', 'Сдано'),
  { key: 'comment', label: 'Комментарий', filterable: false, render: r => r.comment || '—' },
];

// ─── Модули: заголовок, шапка (блок дашборда) и список расшифровок ──────────────────────────
// api — ключ списка на сервере (/finance/ceo/list/:api); kind — какие столбцы и что открывать
// по клику на строку.
const MODULES = {
  allocation: {
    title: 'Разноска',
    lists: [
      { key: 'bank-uncategorized', label: 'Операции из банка без статьи', kind: 'bank' },
      { key: 'requests-unallocated', label: 'Заявки без проекта или статьи', kind: 'request' },
    ],
  },
  pnl: {
    title: 'P&L',
    lists: [
      { key: 'revenue', label: 'Выручка — поступления от клиентов', kind: 'entry' },
      { key: 'expenses', label: 'Расходы', kind: 'entry' },
      { key: 'pnl-articles', label: 'Свод по статьям (маржа)', kind: 'article' },
    ],
  },
  money: {
    title: 'Деньги',
    lists: [
      { key: 'payable', label: 'Кредиторка (к оплате)', kind: 'request' },
      { key: 'overdue', label: 'Просрочено', kind: 'request' },
      { key: 'receivable', label: 'Дебиторка (должны нам)', kind: 'project' },
      { key: 'completed-unpaid', label: 'Завершённые без оплаты', kind: 'project' },
      { key: 'pending', label: 'На подписи', kind: 'request' },
      { key: 'account-entries', label: 'Операции по счёту', kind: 'entry', needs: 'account_id' },
    ],
  },
  projects: {
    title: 'Проекты',
    lists: [
      { key: 'projects-in-progress', label: 'В работе', kind: 'project' },
      { key: 'projects-closed', label: 'Закрыто в периоде', kind: 'project' },
      { key: 'projects-on-hold', label: 'Приостановлено', kind: 'project' },
      { key: 'projects-low-margin', label: 'Маржа ниже порога', kind: 'project' },
    ],
  },
  sales: {
    title: 'Продажи (AmoCRM)',
    lists: [
      { key: 'deals-new', label: 'Новые сделки за период', kind: 'deal' },
      { key: 'deals-open', label: 'Открытые сделки сейчас', kind: 'deal', stages: true },
      { key: 'deals-no-call', label: 'Без ни одного звонка', kind: 'deal' },
      { key: 'deals-first-call', label: 'Время до первого звонка', kind: 'deal' },
    ],
  },
  operations: {
    title: 'Операционка',
    lists: [
      { key: 'queue', api: 'payable', label: 'Очередь на оплату', kind: 'request' },
      { key: 'overdue', label: 'Просрочено', kind: 'request' },
      { key: 'bank-uncategorized', label: 'Неразмечено в банке', kind: 'bank' },
      { key: 'designers-in-work', label: 'Дизайнеры в работе', kind: 'designer' },
      { key: 'designers-done', label: 'Сдано в периоде', kind: 'designer' },
    ],
  },
};

// Сводка открытых сделок по воронкам и стадиям — над списком «Открытые сделки».
function StagesSummary({ rows }) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const k = `${r.pipeline}\u0000${r.stage}`;
      if (!map.has(k)) map.set(k, { pipeline: r.pipeline, stage: r.stage, count: 0, sum: 0 });
      const g = map.get(k); g.count++; g.sum += Number(r.price) || 0;
    }
    return [...map.values()].sort((a, b) => a.pipeline.localeCompare(b.pipeline, 'ru') || b.count - a.count);
  }, [rows]);
  return (
    <div className="table-wrap mb-2">
      <table>
        <thead><tr><th>Воронка</th><th>Стадия</th><th>Сделок</th><th>Сумма</th></tr></thead>
        <tbody>{groups.map(g => <tr key={g.pipeline + g.stage}><td>{g.pipeline}</td><td>{g.stage}</td><td>{g.count}</td><td>{money(g.sum)}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

function ListTable({ list, range, extraParams, onOpenRequest, onOpenProject, reloadKey }) {
  const navigate = useNavigate();
  const params = useMemo(() => ({ ...range, ...extraParams }), [range, extraParams]);
  const state = useBlock(`/finance/ceo/list/${list.api || list.key}`, params, reloadKey);

  const { columns, onRowClick } = useMemo(() => {
    switch (list.kind) {
      case 'request': return { columns: REQUEST_COLUMNS, onRowClick: r => onOpenRequest(r.id) };
      case 'project': return { columns: PROJECT_COLUMNS, onRowClick: r => onOpenProject(r.id) };
      case 'entry': return {
        columns: ENTRY_COLUMNS,
        onRowClick: r => (r.payment_request_id ? onOpenRequest(r.payment_request_id) : r.project_id ? onOpenProject(r.project_id) : null),
      };
      case 'article': return { columns: ARTICLE_COLUMNS, onRowClick: null };
      case 'bank': return { columns: BANK_COLUMNS(navigate), onRowClick: null };
      case 'deal': return { columns: DEAL_COLUMNS, onRowClick: r => r.url && window.open(r.url, '_blank', 'noopener') };
      case 'designer': return { columns: DESIGNER_COLUMNS, onRowClick: r => onOpenProject(r.project_id) };
      default: return { columns: [], onRowClick: null };
    }
  }, [list.kind, navigate, onOpenRequest, onOpenProject]);

  return (
    <BlockState state={state}>
      {(d) => {
        const rows = list.kind === 'deal'
          ? d.rows.map(r => ({ ...r, createdDate: new Date(r.createdAt * 1000).toISOString() }))
          : d.rows;
        return (
          <>
            {list.stages && <StagesSummary rows={rows} />}
            <DataTable
              key={list.key + JSON.stringify(extraParams || {})}
              columns={columns}
              rows={rows}
              onRowClick={onRowClick || undefined}
              canExport
              exportFilename={list.label}
              emptyText="Записей нет"
              renderSummary={list.kind === 'request' ? (visibleRows => <RequestsSummary rows={visibleRows} />) : undefined}
            />
          </>
        );
      }}
    </BlockState>
  );
}

export default function CeoModule() {
  const { module: moduleKey } = useParams();
  const [params, setParams] = useSearchParams();
  const period = usePeriod();
  const go = useGo(period);
  const navigate = useNavigate();
  const [threshold, setThreshold] = useState(Number(params.get('threshold')) || 50);
  const [openRequest, setOpenRequest] = useState(null);
  const [openProject, setOpenProject] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const mod = MODULES[moduleKey];
  if (!mod) return <div className="empty-state">Модуль не найден. <Link to="/finance/dashboard">← К дашборду</Link></div>;

  const listKey = mod.lists.some(l => l.key === params.get('list')) ? params.get('list') : mod.lists[0].key;
  const list = mod.lists.find(l => l.key === listKey);
  const accountId = params.get('account_id');
  const range = period.range;

  const selectList = (key) => setParams(prev => { const n = new URLSearchParams(prev); n.set('list', key); return n; }, { replace: true });
  // Внутри модуля клик по плитке своего же модуля просто переключает список, по чужому —
  // переходит в тот модуль (go сам строит адрес с периодом).
  const goHere = (m, l, extra) => (m === moduleKey && !extra ? selectList(l) : go(m, l, extra));

  const extraParams = list.needs === 'account_id'
    ? { account_id: accountId }
    : listKey === 'projects-low-margin' ? { threshold } : undefined;

  const header = (() => {
    switch (moduleKey) {
      case 'allocation': return <AlertBlock go={goHere} active={listKey} always />;
      case 'pnl': return range && <PnlBlock range={range} go={goHere} active={listKey} />;
      case 'money': return (
        <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
          <BalancesCard go={goHere} active={listKey} accountId={accountId} />
          {range && <DebtsCard range={range} go={goHere} active={listKey} />}
        </div>
      );
      case 'projects': return range && <div style={{ marginBottom: 20 }}><ProjectsColumn range={range} go={goHere} active={listKey} threshold={threshold} setThreshold={setThreshold} /></div>;
      case 'sales': return range && <div style={{ marginBottom: 20 }}><SalesColumn range={range} go={goHere} active={listKey} /></div>;
      case 'operations': return range && <OperationsBlock range={range} go={goHere} active={listKey} />;
      default: return null;
    }
  })();

  const visibleLists = mod.lists.filter(l => !l.needs || (l.needs === 'account_id' && accountId));

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: 12 }}>
        <button className="btn-secondary btn-sm" onClick={() => navigate(`/finance/dashboard?${period.periodQuery}`)}>← CEO-дашборд</button>
        <h2 style={{ margin: '0 0 0 8px' }}>{mod.title}</h2>
      </div>
      <PeriodBar period={period} />
      {header}

      <div className="card">
        <div className="toolbar" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
          {visibleLists.map(l => (
            <button key={l.key} className={l.key === listKey ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'} onClick={() => selectList(l.key)}>
              {l.label}
            </button>
          ))}
        </div>
        {list.needs === 'account_id' && !accountId
          ? <div className="text-muted">Выберите счёт в блоке «Остатки по счетам».</div>
          : range
            ? <ListTable list={list} range={range} extraParams={extraParams} reloadKey={reloadKey}
                onOpenRequest={setOpenRequest} onOpenProject={setOpenProject} />
            : <div className="text-muted">Укажите корректный период.</div>}
      </div>

      {openRequest && (
        <PaymentDetail id={openRequest} onClose={() => setOpenRequest(null)} onChanged={() => setReloadKey(k => k + 1)} />
      )}
      {openProject && (
        <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && setOpenProject(null)}>
          <div className="modal" style={{ maxWidth: 1100, width: '95vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <DealDetail id={openProject} onClose={() => { setOpenProject(null); setReloadKey(k => k + 1); }} />
          </div>
        </div>
      )}
    </div>
  );
}
